/* eslint-disable max-lines */
//
//  SDKDataCoreAIRequests.ts
//  Supernova SDK
//
//  Created by Jiri Trecak.
//  Copyright © 2024 Supernova. All rights reserved.
//
// --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- ---
// MARK: - Imports
import { AIGetRouteQueryTask } from "../../areas/SDKAreaAI";
// --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- ---
// MARK: - Request engine: AI
export class DataCoreAIRequests {
    // --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- ---
    // MARK: - Constructor
    constructor(bridge) {
        this.cachedEndpoints = new Map();
        this.bridge = bridge;
    }
    // --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- ---
    // MARK: - User
    /** Remote: Summarizes a prompt into a title. Response is not streamed */
    async summarizeIntoTitle(workspaceId, designSystemId, versionId, text, options) {
        const endpoint = await this.getCachedAIEndpoint(AIGetRouteQueryTask.summarize);
        const result = await this.bridge.post(endpoint, {
            workspaceId,
            designSystemId,
            versionId,
            text,
            options,
        });
        return result === null || result === void 0 ? void 0 : result.summary;
    }
    /** Remote: Populates Forge instance a code snippet from a prompt. Response is streamed */
    async code(workspaceId, designSystemId, versionId, projectId, participantId, messageType, prompt, options) {
        const endpoint = await this.getCachedAIEndpoint(AIGetRouteQueryTask.code);
        const result = await this.bridge.postStreamed(endpoint, {
            workspaceId,
            designSystemId,
            versionId,
            projectId,
            participantId,
            messageType,
            prompt,
            options,
        });
        return result;
    }
    /** Remote: Autocompletes a prompt into a full text. Response is not streamed. */
    async autocompleteText(workspaceId, designSystemId, versionId, textToComplete, options = { maximumLength: 200, previousText: undefined, afterText: undefined }) {
        const endpoint = await this.getCachedAIEndpoint(AIGetRouteQueryTask.autocomplete);
        const result = await this.bridge.post(endpoint, {
            workspaceId,
            designSystemId,
            versionId,
            textToComplete,
            previousText: options === null || options === void 0 ? void 0 : options.previousText,
            afterText: options === null || options === void 0 ? void 0 : options.afterText,
            options: {
                maximumLength: options.maximumLength,
            },
        });
        return {
            completion: result === null || result === void 0 ? void 0 : result.completion,
            fullText: result === null || result === void 0 ? void 0 : result.fullText,
        };
    }
    /** Remote: Answers question based on the data of design system in natural language. This method is non-streaming. */
    async answer(workspaceId, designSystemId, versionId, question, options) {
        const endpoint = await this.getCachedAIEndpoint(AIGetRouteQueryTask.answer);
        const result = await this.bridge.post(endpoint, {
            workspaceId,
            designSystemId,
            versionId,
            prompt: question,
            options,
        });
        return result === null || result === void 0 ? void 0 : result.answer;
    }
    /** Remote: Answers question based on the data of design system in natural language. This method streams answer. */
    async answerAsStream(workspaceId, designSystemId, versionId, question, options) {
        const endpoint = await this.getCachedAIEndpoint(AIGetRouteQueryTask.answerWithStream);
        const result = await this.bridge.postStreamed(endpoint, {
            workspaceId,
            designSystemId,
            versionId,
            prompt: question,
            options,
        });
        return result;
    }
    /** Indexes or reindexes a design system to prepare it for QA engine */
    async rebuildAnswerIndex(workspaceId, designSystemId, versionId) {
        const endpoint = await this.getCachedAIEndpoint(AIGetRouteQueryTask.reindex);
        await this.bridge.post(endpoint, {
            workspaceId,
            designSystemId,
            versionId,
        });
    }
    /** Retrieves executable url for various AI tasks */
    async executableTaskUrl(task) {
        const endpoint = this.bridge.getAIEndpoint(`ai/get-request-url`);
        const result = await this.bridge.post(endpoint, {
            task,
        });
        return result === null || result === void 0 ? void 0 : result.routeUrl;
    }
    async getCachedAIEndpoint(task) {
        const taskUrl = this.cachedEndpoints.get(task);
        if (taskUrl) {
            return taskUrl;
        }
        const url = await this.executableTaskUrl(task);
        this.cachedEndpoints.set(task, url);
        return url;
    }
}
